<script>
    import { onMount } from "svelte";

    import Select from "svelte-select/Select.svelte";
    import NotificationModal from "../../../Components/Common/NotificationModal.svelte";
    import Loader from "../../../Components/Common/Loader.svelte";
    import { insuranceFund } from "../../../stores";
    import { cacheInsuranceFunds } from "../../../Utils/utilCache.svelte";
    import { apiMethods } from "../../../Services/index.svelte";
    import { showException } from "../../../Utils/utilFunctions.svelte";
    import OpvStatusModal from "./OpvStatusModal.svelte";
    import { insuranceTypeHideQuoteSections } from "../../../Utils/utilConstants.svelte";
    import { DirectInsuranceType } from "../../../Utils/utilEnums.svelte";

    export let patientDetails;
    export let patientEncounter;
    export let isCheckOpvOnDiffFundModalOpen;
    export let onClose;
    export let onConfirmChangeFund;

    let selectedInsuranceFund,
        membershipNo,
        membershipRefNo,
        isLoadingInsuranceFund = false,
        opvVerificationSuccess = false,
        hasInsuranceFundFetched = false,
        isEncounterHasActiveEstimate = false,
        isEncounterHasActiveInvoice = false,
        confirmChangeInsuranceDetailsModalOpen = false,
        isCheckingOPV = false,
        isUpdating = false;

    let opvResult = null;

    onMount(async () => {
        //load insurance funds
        if (!$insuranceFund.length && !hasInsuranceFundFetched) {
            hasInsuranceFundFetched = true;

            isLoadingInsuranceFund = true;

            await cacheInsuranceFunds();

            isLoadingInsuranceFund = false;
        }

        isEncounterHasActiveEstimate =
            _checkIfEncounterHasActiveEstimateOrInvoice(patientEncounter.estimates);

        isEncounterHasActiveInvoice =
            _checkIfEncounterHasActiveEstimateOrInvoice(patientEncounter.invoices);
    })

    $: checkOpvEnabled = checkAllFieldsAreEntered(selectedInsuranceFund, membershipNo);

    $: canChangeInsuranceDetails = 
        patientEncounter.billingStatus == 0 || //prequote
        (patientEncounter.billingStatus == 2 && !isEncounterHasActiveEstimate) || //ready and no quote
        (patientEncounter.billingStatus == 2 &&
                 insuranceTypeHideQuoteSections.includes(patientDetails.insuranceType)); //ready and no quote available by ins type

    $: disabledChangeInsuranceDetailsMessage = _changeInsuranceDetailsDisabledMessage(patientEncounter.billingStatus, isEncounterHasActiveEstimate, isEncounterHasActiveInvoice);

    const _changeInsuranceDetailsDisabledMessage = (
        billingStatus,
        activeEstimate,
        activeInvoice,
    ) => {
        if (activeEstimate && (billingStatus == 1 || billingStatus == 2))
            return "Certain patient details cannot be changed after a quote has been submitted. To enable editing, please cancel the quote.";
        else if (activeInvoice) {
            if (billingStatus == 3)
                return "Certain patient details cannot be changed after and invoice has been submitted. To enable editing, please change the encounter status to Ready in the billing tab.";
            else if (billingStatus == 4 || billingStatus == 5)
                return "Certain patient details cannot be changed after an invoice has been submitted.";
            else
                return "Certain patient details cannot be changed after a quote or an invoice were submitted. To enable editing, please cancel the encounter estimates and invoices";
        } else
            return "Certain patient details cannot be changed after the encounter is submitted. To enable editing, please change the encounter billing status back to Ready.";
    };

    const checkOpvOnNewFund = async () => {
        if (isCheckingOPV) return;
        isCheckingOPV = true;

        var newPatientDetails = {
            ...patientDetails
        }

        newPatientDetails = {
            ...newPatientDetails,
            //copy insurance fund membership number and membership ref number on patient
            insuranceType: 0, //private if not already
            insuranceId: selectedInsuranceFund.id,
            membershipNo: membershipNo,
            membershipRefNo: membershipRefNo,
        };

        if (newPatientDetails.medicareRefNo)
            newPatientDetails.medicareRefNo = Number(newPatientDetails.medicareRefNo);

        newPatientDetails.title =
            newPatientDetails.title.toString().toLowerCase() == "title"
                ? 20
                : newPatientDetails.title;

        //check opv
        await apiMethods.postMethod
            .checkOPV(newPatientDetails, true)
            .then((res) => {
                opvResult = { ...res, insuranceType: newPatientDetails.insuranceType };

                opvVerificationSuccess = 
                   opvResult.insuranceType == DirectInsuranceType.DVA
                        ? opvResult.veteranStatus == 1
                        : opvResult.insuranceType == DirectInsuranceType.Private
                        ? opvResult.medicareStatus != 2 && opvResult.healthFundStatus != 2
                        : opvResult.medicareStatus != 2; 
            })
            .catch((err) => showException(err));

        isCheckingOPV = false;
    }

    const checkAllFieldsAreEntered = (insFund, membNo) => {
        return insFund != null && membNo;
    }

    const _checkIfEncounterHasActiveEstimateOrInvoice = (arr) =>
        arr && arr.length && arr.some((e) => e.status != 9);

</script>

<NotificationModal
    title="Check OPV on different fund"
    isModalOpen={isCheckOpvOnDiffFundModalOpen}
    closeModal={onClose}
    closeModalFunction={onClose}
    cancelText="Cancel"
    showConfirmBtn={false}
    btnOptions={[
        !opvResult ? {
            text: isCheckingOPV ? "Checking.." : "Check OPV",
            loading: isCheckingOPV,
            isDisabled: !checkOpvEnabled,
            tooltip: !checkOpvEnabled ? "Please select insurance fund and enter membership details" : "",
            function: checkOpvOnNewFund,
        } : null,
        opvResult && opvVerificationSuccess ?
        {
            text: isUpdating ? "Updating insurance details.." : "Change insurance details",
            isDisabled: !canChangeInsuranceDetails,
            loading: isUpdating,
            tooltip: !canChangeInsuranceDetails && !isUpdating ? disabledChangeInsuranceDetailsMessage : "",
            function: async () => {
                confirmChangeInsuranceDetailsModalOpen = true;
            },
        } : null,
    ]}
>
    <div class="flex w-full flex-col gap-y-4">
        <div class="flex-1 flex flex-col gap-y-1">
            <p class="title">New insurance fund</p>

            <div
                class="w-full"
                id="insuranceFundContainer"
            >
                {#if isLoadingInsuranceFund}
                    <div class="flex gap-x-2">
                        <p>Loading Insurance Fund</p>
                        <Loader />
                    </div>
                {:else}
                    <Select
                        bind:value={selectedInsuranceFund}
                        id="insuranceFundSelect"
                        class="flex flex-1 max-h-[35px] rounded-full"
                        items={$insuranceFund}
                        itemId="id"
                        label="name"
                        on:clear={() => {
                            selectedInsuranceFund = null;
                            opvResult = null;
                        }}
                        on:select={() => opvResult = null }
                        noOptionsMessage="No insurance found"
                        placeholder="Insurance fund"
                        --placeholder-color="#9CA3AF"
                        --height="35px"
                        --value-container-padding="0"
                        --padding="0 0 0 12px"
                        --font-size="14px"
                    />
                {/if}
            </div>
        </div>
        <div class="w-full flex-1 flex flex-col gap-y-1">
            <p class="title">Membership Number</p>
            <input
                type="text"
                id="insuranceDetailsMembershipNoField"
                placeholder="Membership No"
                class="input input-sm input-bordered h-[35px]"
                autocomplete="off"
                bind:value={membershipNo}
                on:change={() => opvResult = null }
                on:focus={({ target }) => {
                    target.style.outline = "";
                    target.placeholder = "Membership No";
                }}
            />
        </div>
        <div class="w-full flex-1 flex flex-col gap-y-1">
            <p class="title">Membership Ref Number</p>
            <input
                type="text"
                id="insuranceDetailsMembershipRefNoField"
                placeholder="Membership Ref No"
                class="input input-sm input-bordered h-[35px]"
                autocomplete="off"
                bind:value={membershipRefNo}
                on:change={() => opvResult = null }
                on:focus={({ target }) => {
                    target.style.outline = "";
                    target.placeholder = "Membership Ref No";
                }}
            />
        </div>
        <div>
            <OpvStatusModal
                showAsModal={false}
                opvResult={opvResult}
            ></OpvStatusModal>
        </div>
    </div>

</NotificationModal>

<NotificationModal
    isModalOpen={confirmChangeInsuranceDetailsModalOpen}
    cancelText="Cancel"
    submitText="Confirm"
    confirmAction={async () => {
        isUpdating = true;
        await onConfirmChangeFund(selectedInsuranceFund.id, membershipNo, membershipRefNo);
        confirmChangeInsuranceDetailsModalOpen = false;
    }}
    closeModal={() => confirmChangeInsuranceDetailsModalOpen = false }
    closeModalFunction={() => confirmChangeInsuranceDetailsModalOpen = false }
>
    <p class="text-error">
        Are you sure you want to change patient insurance type to Private and fund {selectedInsuranceFund.name} and set the membership details?.
        Changing insurance type or fund details may result in billing rules or items being cleared.
    </p>
</NotificationModal>
